import { FC, useEffect, useRef } from "react";
import { useData } from "data/data-provider";
import DbIcon from "designs/DesignB/components/icons";
import CarDetailsBody from "./CarDetailsBody";
import SwipeGallery from "./SwipeGallery";
import { descriptionText, detailSpecs, featureNames, galleryPhotos } from "./carDetails";

/**
 * The car's details on the Design B car page (1NOW-11219), for a renter who came straight to the
 * page (an ad, a shared link, a reload) and never saw the fleet card's panel: the panel's photos,
 * specs, description and features in a native modal dialog, without its price and Book now (the
 * renter is booking already). The dialog is always in the page and only drawn while open, so its
 * text stays in the page for search engines as the old Vehicle Information rows did; the photos
 * load only once it opens. Escape, the close button or a click outside closes it.
 */
const CarDetailsDialog: FC<{ car: any; open: boolean; titleId: string; onClose: () => void }> = ({
  car,
  open,
  titleId,
  onClose,
}) => {
  const { ourCompany } = useData();
  const ref = useRef<HTMLDialogElement>(null);
  const specs = detailSpecs(car, ourCompany);
  const description = descriptionText(car?.description);
  const features = featureNames(car);

  useEffect(() => {
    const d = ref.current;
    if (!d || !open) return;
    // jsdom has no showModal: the attribute is what it renders by.
    if (d.showModal) d.showModal();
    else d.setAttribute("open", "");
    document.documentElement.classList.add("db-sheet-open");
    return () => {
      document.documentElement.classList.remove("db-sheet-open");
      if (d.close) d.close();
      else d.removeAttribute("open");
    };
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      data-testid="car-details-dialog"
      onClose={onClose}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      className="m-auto max-h-[calc(100dvh-48px)] w-[min(960px,calc(100vw-48px))] overflow-y-auto overscroll-contain rounded-[var(--db-r-card)] border border-[color:var(--db-line)] bg-[var(--db-card)] p-0 pb-2 text-[color:var(--db-ink)] shadow-[var(--db-shadow-lift)] backdrop:bg-black/50"
    >
      <div className="db-xp-main">
        <div className="db-xp-top">
          <h2 className="db-xp-name" id={titleId} title={car?.name}>
            {car?.name}
          </h2>
          <button type="button" className="db-xp-x" aria-label="Close details" onClick={onClose}>
            <DbIcon name="close" className="h-[22px] w-[22px]" />
          </button>
        </div>
        <div className="db-xp-media">{open && <SwipeGallery photos={galleryPhotos(car)} name={car?.name ?? "this car"} />}</div>
        <div className="db-xp-body">
          {/* Mounted again on open: the description measures its Read more where it can be seen. */}
          <CarDetailsBody key={String(open)} part="main" specs={specs} description={description} features={features} />
        </div>
        <CarDetailsBody part="features" specs={specs} description={description} features={features} />
      </div>
    </dialog>
  );
};

export default CarDetailsDialog;
